/**
 * One supplier invoice: the capture form over whatever the XML parser
 * extracted, its Conceptos, and its payment history.
 *
 * The form is the source of truth — the parser only saves typing. A PDF-only
 * invoice arrives here entirely blank and in `pendiente`, and cannot be paid
 * until the fields are filled (the server enforces that too).
 */
import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import { currentUser } from '../../api/auth';
import {
  cancelPayable, fetchPayable, payableFileUrl, recordApPayment,
  savePayable, voidApPayment,
} from '../../api/payables';
import type { ApLine, PayableDetail as Detail, User } from '../../api/types';
import { payablesPerms } from '../../lib/payablesPerms';
import { t } from '../../lib/i18n';
import AccessDenied from '../../components/AccessDenied';
import Header from '../../components/Header';
import ErrorRetry from '../../components/ErrorRetry';
import TableSkeleton from '../../components/TableSkeleton';
import { money, statusBadge } from './Payables';

const emptyLine = (seq: number): ApLine => ({
  id: '', invoice_id: '', seq, cantidad: 0, unidad: '', no_identificacion: '',
  descripcion: '', valor_unitario: 0, descuento: 0, importe: 0,
});

/** Header fields the capture form edits, paired with their Spanish labels. */
const FIELDS = [
  ['folio', t.payables.folio],
  ['fecha', t.payables.fecha],
  ['due_date', t.payables.vence],
  ['moneda', t.payables.moneda],
  ['tipo_cambio', t.payables.tipoCambio],
  ['subtotal', t.payables.subtotal],
  ['impuestos', t.payables.impuestos],
  ['total', t.payables.total],
] as const;

function PayableDetailInner() {
  const { id = '' } = useParams();
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [data, setData] = useState<Detail | null>(null);
  const [lines, setLines] = useState<ApLine[]>([]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('transferencia');
  const [payRef, setPayRef] = useState('');
  const [payBank, setPayBank] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);

  const perms = payablesPerms(user);

  const load = () => {
    setErr(null);
    fetchPayable(id)
      .then((d) => {
        setData(d);
        setLines(d.lines);
        setForm({
          rfc: d.invoice.rfc,
          emisor_nombre: d.invoice.emisor_nombre,
          serie: d.invoice.serie,
          folio: d.invoice.folio,
          fecha: d.invoice.fecha,
          due_date: d.invoice.due_date,
          moneda: d.invoice.moneda,
          tipo_cambio: String(d.invoice.tipo_cambio),
          subtotal: String(d.invoice.subtotal),
          impuestos: String(d.invoice.impuestos),
          descuento: String(d.invoice.descuento),
          total: String(d.invoice.total),
          notes: d.invoice.notes,
        });
      })
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(() => {
    if (perms.canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms.canView, id]);

  if (user === undefined) return <TableSkeleton />;
  if (!user || !perms.canView) {
    return <AccessDenied user={user ?? ({ username: '', role: 'staff', sid_assigned: null } as User)} />;
  }
  if (err && !data) return <ErrorRetry message={err} onRetry={load} />;
  if (!data) return <TableSkeleton />;

  const inv = data.invoice;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setErr(null);
    try {
      await savePayable(id, {
        rfc: form.rfc,
        emisor_nombre: form.emisor_nombre,
        serie: form.serie,
        folio: form.folio,
        fecha: form.fecha,
        due_date: form.due_date,
        moneda: form.moneda,
        tipo_cambio: Number(form.tipo_cambio) || 1,
        subtotal: Number(form.subtotal) || 0,
        impuestos: Number(form.impuestos) || 0,
        descuento: Number(form.descuento) || 0,
        total: Number(form.total) || 0,
        tipo_comprobante: inv.tipo_comprobante,
        notes: form.notes,
        lines: lines.map((l, i) => ({
          seq: i + 1,
          cantidad: Number(l.cantidad) || 0,
          unidad: l.unidad,
          no_identificacion: l.no_identificacion,
          descripcion: l.descripcion,
          valor_unitario: Number(l.valor_unitario) || 0,
          descuento: Number(l.descuento) || 0,
          importe: Number(l.importe) || 0,
        })),
      });
      load();
    } catch (e) { setErr((e as Error).message); }
  };

  const doCancel = async () => {
    setErr(null);
    try { await cancelPayable(id); load(); } catch (e) { setErr((e as Error).message); }
  };

  const submitPayment = async () => {
    setErr(null);
    const amount = Number(payAmount) || 0;
    try {
      await recordApPayment({
        paid_at: payDate,
        moneda: inv.moneda,
        amount,
        method: payMethod,
        reference: payRef,
        bank: payBank,
        notes: '',
        applications: [{ invoice_id: id, amount }],
      });
      setPaying(false);
      setPayAmount('');
      load();
    } catch (e) { setErr((e as Error).message); }
  };

  const doVoid = async (paymentId: string) => {
    setErr(null);
    // The reason lands in the audit log, so send a real sentence rather than
    // a field label. A typed reason is a v2 refinement.
    try { await voidApPayment(paymentId, t.payables.voidDefaultReason); load(); }
    catch (e) { setErr((e as Error).message); }
  };

  const payable = inv.status === 'abierta' || inv.status === 'parcial';

  const setLine = (i: number, patch: Partial<ApLine>) =>
    setLines((ls) => ls.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Title>{t.payables.factura} {inv.serie} {inv.folio}</Title>
        <div className="flex gap-3 items-center">
          {statusBadge(inv.status)}
          {inv.xml_ref ? (
            <a href={payableFileUrl(id, 'xml')}>{t.payables.descargarXml}</a>
          ) : null}
          {inv.pdf_ref ? (
            <a href={payableFileUrl(id, 'pdf')}>{t.payables.descargarPdf}</a>
          ) : null}
        </div>
      </div>

      {inv.warnings ? <Card><Text color="amber">{inv.warnings}</Text></Card> : null}
      {err ? <Card><Text color="red">{err}</Text></Card> : null}

      <Card>
        <Text>{t.payables.proveedor}: {inv.emisor_nombre || inv.rfc}</Text>
        {inv.onum ? (
          <Text>
            {t.payables.orden}: <Link to={`/compras?onum=${inv.onum}`}>{inv.onum}</Link>
          </Text>
        ) : null}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-sm">{label}</span>
              <TextInput
                aria-label={label}
                value={form[key] ?? ''}
                onChange={set(key)}
                disabled={!perms.canCapture}
              />
            </label>
          ))}
        </div>
        {perms.canCapture ? (
          <div className="mt-4 flex gap-2">
            <Button onClick={save}>{t.payables.guardar}</Button>
            {inv.status !== 'cancelada' ? (
              <Button variant="secondary" color="red" onClick={doCancel}>
                {t.payables.cancelInvoice}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card>
        <Title>{t.payables.conceptos}</Title>
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.payables.cantidad}</TableHeaderCell>
                <TableHeaderCell>{t.payables.unidad}</TableHeaderCell>
                <TableHeaderCell>{t.payables.descripcion}</TableHeaderCell>
                <TableHeaderCell>{t.payables.valorUnitario}</TableHeaderCell>
                <TableHeaderCell>{t.payables.importe}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l.id || `new-${i}`}>
                  <TableCell>
                    <TextInput
                      aria-label={`Cantidad fila ${i + 1}`}
                      value={String(l.cantidad)}
                      disabled={!perms.canCapture}
                      onChange={(e) => setLine(i, { cantidad: Number(e.target.value) || 0 })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextInput
                      aria-label={`Unidad fila ${i + 1}`}
                      value={l.unidad}
                      disabled={!perms.canCapture}
                      onChange={(e) => setLine(i, { unidad: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextInput
                      aria-label={`Descripción fila ${i + 1}`}
                      value={l.descripcion}
                      disabled={!perms.canCapture}
                      onChange={(e) => setLine(i, { descripcion: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextInput
                      aria-label={`Precio fila ${i + 1}`}
                      value={String(l.valor_unitario)}
                      disabled={!perms.canCapture}
                      onChange={(e) => setLine(i, { valor_unitario: Number(e.target.value) || 0 })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextInput
                      aria-label={`Importe fila ${i + 1}`}
                      value={String(l.importe)}
                      disabled={!perms.canCapture}
                      onChange={(e) => setLine(i, { importe: Number(e.target.value) || 0 })}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {perms.canCapture ? (
          <Button
            variant="light"
            className="mt-2"
            onClick={() => setLines((ls) => [...ls, emptyLine(ls.length + 1)])}
          >
            {t.payables.addLine}
          </Button>
        ) : null}
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <Title>{t.payables.pagos}</Title>
          <Text>{t.payables.saldo}: {money(inv.saldo, inv.moneda)}</Text>
        </div>

        {data.payments.length > 0 ? (
          <div className="overflow-x-auto mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.payables.fecha}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.metodo}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.referencia}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.importe}</TableHeaderCell>
                  <TableHeaderCell> </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.paid_at}</TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell>{p.reference}</TableCell>
                    <TableCell>
                      {money(p.applications[0]?.amount ?? p.amount, p.moneda)}
                    </TableCell>
                    <TableCell>
                      {p.voided_at ? (
                        <Badge color="gray">{t.payables.anulado}</Badge>
                      ) : perms.canPay ? (
                        <Button variant="light" onClick={() => doVoid(p.id)}>
                          {t.payables.anular}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}

        {perms.canPay && payable && !paying ? (
          <Button className="mt-3" onClick={() => setPaying(true)}>
            {t.payables.registrarPago}
          </Button>
        ) : null}

        {paying ? (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-sm">{t.payables.fecha}</span>
              <TextInput
                aria-label={`${t.payables.fecha} del pago`}
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm">{t.payables.importe}</span>
              <TextInput
                aria-label={t.payables.importe}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm">{t.payables.metodo}</span>
              <TextInput
                aria-label={t.payables.metodo}
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm">{t.payables.referencia}</span>
              <TextInput
                aria-label={t.payables.referencia}
                value={payRef}
                onChange={(e) => setPayRef(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm">{t.payables.banco}</span>
              <TextInput
                aria-label={t.payables.banco}
                value={payBank}
                onChange={(e) => setPayBank(e.target.value)}
              />
            </label>
            <div className="flex items-end gap-2">
              <Button onClick={submitPayment}>{t.payables.guardarPago}</Button>
              <Button variant="secondary" onClick={() => setPaying(false)}>
                {t.payables.cancelar}
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Page shell. Every routed page in this app renders its OWN Header + <main>
 * (App.tsx has no shared authed layout), so without this the payables pages
 * had no nav bar at all — you could reach /pagar and not navigate away — and
 * sat flush against the window edges with no padding.
 */
export default function PayableDetail() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {payablesPerms(user).canView ? <PayableDetailInner /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}
